import { ProForm } from '@ant-design/pro-components'
import type SignatureCanvas from '@pkg/ui/signature/SignatureCanvas'
import SignaturePad, { type SignaturePadProps } from '@pkg/ui/signature/SignaturePad'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { RenderErrors } from '../components/RenderErrors'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'

export type SignaturePadFieldProps = Omit<SignaturePadProps, 'onChange' | 'ref'> & {
  label?: React.ReactNode | false
  className?: string
}

export function SignaturePadField(props: SignaturePadFieldProps) {
  const fieldCtx = useFieldContext<string | null | undefined>()
  const { className, label, ...rest } = props
  const editable = useEditableFieldMode<string | null | undefined>()
  const signatureRef = useRef<SignatureCanvas | null>(null)

  const value = fieldCtx.state.value
  const errors = fieldCtx.state.meta.errors
  const skipNextEffect = useRef(false)

  useEffect(() => {
    const signatureCanvas = signatureRef.current
    if (!signatureCanvas) {
      return
    }

    if (skipNextEffect.current) {
      skipNextEffect.current = false
      return
    }

    if (value) {
      const canvas = signatureCanvas.getCanvas()
      void signatureCanvas.fromDataURL(value, { width: canvas.width, height: canvas.height })
      return
    }

    signatureCanvas.clear()
  }, [value])

  const handleSignatureChange = (signatureCanvas?: SignatureCanvas | null) => {
    const canvas = signatureCanvas ?? signatureRef.current
    if (!canvas || canvas.isEmpty()) {
      fieldCtx.handleChange(undefined)
      return
    }

    skipNextEffect.current = true
    fieldCtx.handleChange(canvas.toDataURL())
  }

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProForm.Item
        label={
          label === false ? undefined : (
            <EditableLabel
              mode={editable.mode}
              enterEdit={editable.enterEdit}
              exitRead={editable.exitRead}
              onUndo={editable.undoAndExit}
              canUndo={editable.canUndo}
            >
              {label}
            </EditableLabel>
          )
        }
        validateStatus={errors.length > 0 ? 'error' : undefined}
        help={<RenderErrors errors={errors} />}
      >
        {editable.mode === 'read' ? (
          value ? (
            <img src={value} alt="Signature" className="max-w-full h-auto" />
          ) : (
            '-'
          )
        ) : (
          <SignaturePad {...rest} ref={signatureRef} onChange={handleSignatureChange} />
        )}
      </ProForm.Item>
    </EditableShell>
  )
}
